import { useState } from 'react';
import type { AssistantExportable, AssistantExportToSheet } from './types';
import { isAssistantError } from './types';

/**
 * Renders the "Export to Google Sheet" affordance for one answer. Owns its own
 * pending/error/result state so each answer's export is independent. Calls the
 * host-supplied exportToSheet with the answer's opaque `exportable` descriptor,
 * then swaps the button for an "Open sheet" link. Domain-free.
 */
export function ExportSheetButton({
  exportable,
  exportToSheet,
}: {
  exportable: AssistantExportable;
  exportToSheet: AssistantExportToSheet;
}) {
  const [pending, setPending] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const { url: sheetUrl } = await exportToSheet(exportable);
      setUrl(sheetUrl);
    } catch (e) {
      setError(
        isAssistantError(e) ? e.message : "Couldn't export to Google Sheet — please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="ra-export-link mt-2 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
      >
        Open sheet
      </a>
    );
  }

  return (
    <div className="ra-export mt-2">
      <button
        type="button"
        onClick={() => void run()}
        disabled={pending}
        className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
      >
        {pending ? 'Exporting…' : 'Export to Google Sheet'}
      </button>
      {error && (
        <p role="alert" className="mt-1 text-[11px] text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
